import { useRouter } from 'next/router';
import { useEffect, useState, useRef } from 'react';

type PageTransitionProps = {
  children: React.ReactNode;
};

export default function PageTransition({ children }: PageTransitionProps) {
  const router = useRouter();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayChildren, setDisplayChildren] = useState(children);
  const previousPathRef = useRef(router.asPath);

  useEffect(() => {
    const handleStart = (url: string) => {
      // Extract base path without query params for comparison
      const newBasePath = url.split('?')[0];
      const currentBasePath = previousPathRef.current.split('?')[0];

      // Only transition if the base path changes (not just query params like tabId)
      if (newBasePath !== currentBasePath) {
        setIsTransitioning(true);
      }
    };

    const handleComplete = () => {
      setIsTransitioning(false);
      setDisplayChildren(children);
      previousPathRef.current = router.asPath;
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  }, [router, children]);

  // Update children when not transitioning
  useEffect(() => {
    if (!isTransitioning) {
      setDisplayChildren(children);
    }
  }, [children, isTransitioning]);

  return (
    <div
      className={`transition-all duration-200 ease-out ${
        isTransitioning
          ? 'opacity-0 translate-y-2'
          : 'opacity-100 translate-y-0'
      }`}
      style={{ minHeight: '100vh' }}
    >
      {displayChildren}
    </div>
  );
}
